// src/api/openai.js
import { db } from '../firebase';
import { collection, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';

const OPENAI_API_KEY = process.env.REACT_APP_OPENAI_API_KEY;

// ✨ Format templates by category
const formatPrompts = {
    All: `Please structure your response using the following format:\n
S//
Presenting Complaint:

History of Presenting Complaint:

Aggravating Factors:

Easing Factors:

24-hour Behaviour:

Special Questions (Red Flags Screening):

Thoughts and Beliefs about Problem:

Emotional Well-being:

Past MSK History:

Previous Investigations:

Medical History:

Medications:

Lifestyle Factors:

Social History:

Expectations and Goals:

O//
Observation:

Active Movements:

Outcome measure (Questionnaire):

Passive Movements:

Resisted Movements:

Special Tests:

Palpation:

Neurological Exam (if needed):

A//
Working Hypothesis:

Stage:

Classification: 
[Impairment of movement / Impairment of control / Pain behaviours / Deconditioning]

Differential Diagnosis:

Contributing Factors:

Client’s Key Problems:

P//
Immediate Plan:

Short-term Goals:

Long-term Goals:

I//
Treatment Today:

Home Program:

E//
Client's Response to Treatment:

R//
Plan for Next Session:\n`,

    Subjective: `Please write a Subjective assessment (S//) in physiotherapy note style.`,
    Objective: `Please write an Objective assessment (O//) for physiotherapy.`,
    Assessment: `Please write an Assessment (A//) based on physiotherapy findings.`,
    Plan: `Please write a Plan (P//) for physiotherapy management.`,
    Intervention: `Please write an Intervention (I//) of today’s treatment.`,
    Evaluation: `Please write an Evaluation (E//) showing client’s response.`,
    Revision: `Please write a Revision (R//) with plan for next session.`,
};

export async function fetchSOAPNoteResponse(promptText, category = 'All') {
    try {
        const snapshot = await getDocs(collection(db, 'templateBank'));
        const templates = snapshot.docs.map(doc => doc.data());

        const format = formatPrompts[category] || '';
        const messages = [
            { role: 'system', content: 'You are a helpful physiotherapy documentation assistant.' },
            {
                role: 'user',
                content: `Here are some common templates:\n${templates.map(t => `- ${t.title}: ${t.content}`).join('\n')}`
            },
            {
                role: 'user',
                content: `${format}\n\nBased on this, generate a note for:\n${promptText}`
            }
        ];

        const response = await fetch('https://api.openai.com/v1/chat/completions', {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${OPENAI_API_KEY}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                model: 'gpt-4',
                messages,
                temperature: 0.7
            })
        });

        const data = await response.json();
        const aiOutput = data.choices?.[0]?.message?.content || '❌ No response from AI';

        await addDoc(collection(db, 'suggestionLogs'), {
            prompt: promptText,
            category,
            aiOutput,
            createdAt: serverTimestamp()
        });

        return aiOutput;
    } catch (error) {
        console.error('AI SOAP Note Fetch Error:', error);
        return '⚠️ Error fetching SOAP note suggestion.';
    }
}

export async function fetchPublicChatbotResponse(promptText) {
    try {
        const systemPrompt = '你係 TheraVan 嘅社區健康小助手，請用廣東話親切而清晰咁回答問題。你只可以回答關於社區物理治療、健康教育、上門服務、服務流程等常見問題，唔可以提供任何診斷或治療意見。';

        const messages = [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: promptText }
        ];

        const response = await fetch('https://api.openai.com/v1/chat/completions', {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${OPENAI_API_KEY}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                model: 'gpt-4',
                messages,
                temperature: 0.7
            })
        });

        const data = await response.json();
        const aiOutput = data.choices?.[0]?.message?.content || '❌ No response from AI';

        await addDoc(collection(db, 'chatbotLogs'), {
            prompt: promptText,
            aiOutput,
            createdAt: serverTimestamp()
        });

        return aiOutput;
    } catch (error) {
        console.error('AI Chatbot Fetch Error:', error);
        return '⚠️ 出錯了，請稍後再試。';
    }
}
